import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppSelector } from "@/store/store-hooks";
import {
  convertIElementToThreePositionParsed,
  selectIElementWorldMatrix4,
} from "@/utils/transform-conversion-parsed";
import {
  Area2Icon,
  CoordinatesXIcon,
  CoordinatesYIcon,
  CoordinatesZIcon,
  DistanceHorizontalIcon,
  DistanceVerticalIcon,
  FaroText,
  FaroTooltip,
  FontWeights,
  LengthIcon,
  grey,
  NO_TRANSLATE_CLASS,
  PerimeterIcon,
} from "@faro-lotv/flat-ui";
import { Optional } from "@faro-lotv/foundation";
import { IElementMeasurePolygon } from "@faro-lotv/ielement-types";
import { Box, BoxProps, Stack, SvgIconProps } from "@mui/material";
import { useCallback, useMemo, type JSX } from "react";
import { computeMeasurementToDisplay } from "../../measurements/measure-utils";

type MeasurementValuesFieldProps = {
  /** The measurement whose values we want to show to the user */
  measurement: Pick<
    IElementMeasurePolygon,
    "points" | "isClosed" | "metaDataMap"
  > &
    Optional<Pick<IElementMeasurePolygon, "id">, "id">;
};

/** @returns A stack containing the info about the input measurement (used in annotations) */
export function MeasurementValuesField({
  measurement,
}: MeasurementValuesFieldProps): JSX.Element {
  const worldMatrix = useAppSelector(
    selectIElementWorldMatrix4(measurement.id),
  );
  const points = useMemo(
    () =>
      measurement.points.map((p) =>
        convertIElementToThreePositionParsed(p).applyMatrix4(worldMatrix),
      ),
    [measurement.points, worldMatrix],
  );
  const signedAlongAxis = useAppSelector(
    selectHasFeature(Features.DisplaySignedDeviations),
  );

  const addPrefix = useCallback(
    (value: string) =>
      measurement.metaDataMap?.isEstimatedFrom2DPano ? `~${value}` : value,
    [measurement.metaDataMap?.isEstimatedFrom2DPano],
  );

  const distanceUnit = useDistanceUnit();
  const { perimeter, area, verticalDistance, horizontalDistance, x, y, z } =
    useMemo(() => {
      const components = computeMeasurementToDisplay(
        points,
        measurement.isClosed,
        distanceUnit,
        signedAlongAxis,
      );
      return {
        perimeter: components.fullLength,
        area: components.area,
        verticalDistance: components.verticalDistance,
        horizontalDistance: components.horizontalDistance,
        x: components.x,
        y: components.y,
        z: components.z,
      };
    }, [points, measurement.isClosed, signedAlongAxis, distanceUnit]);

  return (
    <Box component="div">
      <FaroText
        variant="labelM"
        sx={{ color: grey[300], pb: 1, fontWeight: FontWeights.Bold }}
      >
        Measurement values
      </FaroText>
      <GridContainer>
        {area && (
          <MeasurementValue
            Icon={Area2Icon}
            tooltip="Area"
            value={addPrefix(area)}
          />
        )}
        <MeasurementValue
          Icon={measurement.isClosed ? PerimeterIcon : LengthIcon}
          tooltip={measurement.isClosed ? "Perimeter" : "Length"}
          value={addPrefix(perimeter)}
        />
        {horizontalDistance !== undefined && (
          <MeasurementValue
            tooltip="Horizontal"
            Icon={DistanceHorizontalIcon}
            value={addPrefix(horizontalDistance)}
          />
        )}
        {verticalDistance !== undefined && (
          <MeasurementValue
            tooltip="Vertical"
            Icon={DistanceVerticalIcon}
            value={addPrefix(verticalDistance)}
          />
        )}
        {x !== undefined && (
          <MeasurementValue
            tooltip="X"
            Icon={CoordinatesXIcon}
            value={addPrefix(x)}
          />
        )}
        {y !== undefined && (
          <MeasurementValue
            tooltip="Y"
            Icon={CoordinatesYIcon}
            value={addPrefix(y)}
          />
        )}
        {z !== undefined && (
          <MeasurementValue
            tooltip="Z"
            Icon={CoordinatesZIcon}
            value={addPrefix(z)}
          />
        )}
      </GridContainer>
    </Box>
  );
}

/**
 * @returns A grid container implemented using a div, instead of
 * the MUI Grid to have fine control on the resizing behavior
 */
function GridContainer({
  children,
  sx,
  ...props
}: Omit<BoxProps, "component">): JSX.Element {
  return (
    <Box
      component="div"
      sx={{
        display: "grid",
        gridTemplateColumns: "auto auto auto",
        rowGap: 1,
        columnGap: 1,
        pt: 1,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
}

/** The size for the icons used in the measurement values field */
const ICON_SIZE_STYLE = {
  width: "18px",
  height: "18px",
};

type MeasurementValueProps = {
  /** The icon nearby the component value */
  Icon(props: SvgIconProps): JSX.Element;
  /** The text show in the tooltip */
  tooltip: string;
  /** The value of the current component */
  value: string;
};

/**
 * @returns The grid item rendering the icon and value of a specific measurement component
 */
function MeasurementValue({
  Icon,
  tooltip,
  value,
}: MeasurementValueProps): JSX.Element {
  return (
    <Stack
      direction="row"
      sx={{
        textAlign: "center",
        justifyContent: "left",
      }}
    >
      <FaroTooltip title={tooltip} sx={ICON_SIZE_STYLE}>
        <Icon sx={{ ...ICON_SIZE_STYLE, color: grey[0] }} />
      </FaroTooltip>
      <FaroText
        variant="bodyS"
        sx={{ pl: 1 }}
        color={grey[0]}
        className={NO_TRANSLATE_CLASS}
      >
        {value}
      </FaroText>
    </Stack>
  );
}
